import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/useAppStore';
import { showSnackbar } from '../../store/snackbarSlice';
import { getPusher } from '../../lib/pusher';
import { platformApi } from '../../store/api/platformApi';

/**
 * Hook que escucha en private-user-{userId} el evento `bug_report.new` y
 * muestra una notificación al rol 'booster_support'. Para otros roles no hace nada.
 *
 * Llamarlo en un punto montado siempre durante la sesión CRM (ej. DashboardLayout).
 */
export function useSupportBugAlerts() {
  const role        = useAppSelector((s) => s.auth.user?.role);
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const dispatch    = useDispatch();
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (role !== 'booster_support' || !accessToken || subscribedRef.current) return;

    let userId: string;
    try {
      userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string;
    } catch {
      return;
    }

    const pusher  = getPusher(accessToken);
    const channel = pusher.subscribe(`private-user-${userId}`);

    const onBugNew = (data: { reportId: string; userName: string; preview: string }) => {
      dispatch(showSnackbar({
        message:  `🐞 Nuevo bug de ${data.userName}: ${data.preview.slice(0, 80)}`,
        severity: 'warning',
      }));
      // Invalidar tag para que el inbox se refresque si está abierto
      dispatch(platformApi.util.invalidateTags(['BugReport']));
      // Si el browser permite notificaciones nativas y la pestaña está oculta:
      if (typeof document !== 'undefined' && document.hidden && 'Notification' in window
          && Notification.permission === 'granted') {
        new Notification('Nuevo bug reportado en Imperium', {
          body: `${data.userName}: ${data.preview.slice(0, 100)}`,
          icon: '/icon-192.png',
        });
      }
    };

    channel.bind('bug_report.new', onBugNew);
    subscribedRef.current = true;

    return () => {
      channel.unbind('bug_report.new', onBugNew);
      pusher.unsubscribe(`private-user-${userId}`);
      subscribedRef.current = false;
    };
    // navigate is stable from react-router; no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, accessToken, dispatch]);
}
